import React from 'react';
import { Link } from 'react-router-dom';

type ColorScheme = 'blue' | 'green' | 'purple' | 'orange' | 'red';

interface AdminCard {
  id: string;
  title: string;
  description: string;
  to: string;
  icon: React.ReactNode;
  colorScheme: ColorScheme;
}

export default function ManagementPage() {
  // Admin hub with quick links to various tools
  const adminCards: AdminCard[] = [
    {
      id: 'styles',
      title: 'Writing Styles',
      description: 'Manage available writing styles',
      to: '/admin/styles',
      colorScheme: 'blue',
      icon: (
        <svg className="h-8 w-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5" />
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M16.5 3.5a2.121 2.121 0 013 3L12 14l-4 1 1-4 7.5-7.5z" />
        </svg>
      )
    },
    {
      id: 'logs',
      title: 'System Logs',
      description: 'View and monitor system logs and activity',
      to: '/admin/logs',
      colorScheme: 'red',
      icon: (
        <svg className="h-8 w-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
        </svg>
      )
    },
    {
      id: 'info',
      title: 'System Info',
      description: 'View build details and system information',
      to: '/admin/info',
      colorScheme: 'orange',
      icon: (
        <svg className="h-8 w-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
        </svg>
      )
    }
  ];

  const getColorClasses = (scheme: ColorScheme) => {
    switch (scheme) {
      case 'blue':
        return {
          iconBox: 'bg-blue-100 dark:bg-blue-900/20 text-blue-600 dark:text-blue-400',
          hover: 'hover:bg-blue-50 dark:hover:bg-blue-900/10',
          border: 'hover:border-blue-200 dark:hover:border-blue-800'
        };
      case 'green':
        return {
          iconBox: 'bg-green-100 dark:bg-green-900/20 text-green-600 dark:text-green-400',
          hover: 'hover:bg-green-50 dark:hover:bg-green-900/10',
          border: 'hover:border-green-200 dark:hover:border-green-800'
        };
      case 'purple':
        return {
          iconBox: 'bg-purple-100 dark:bg-purple-900/20 text-purple-600 dark:text-purple-400',
          hover: 'hover:bg-purple-50 dark:hover:bg-purple-900/10',
          border: 'hover:border-purple-200 dark:hover:border-purple-800'
        };
      case 'orange':
        return {
          iconBox: 'bg-orange-100 dark:bg-orange-900/20 text-orange-600 dark:text-orange-400',
          hover: 'hover:bg-orange-50 dark:hover:bg-orange-900/10',
          border: 'hover:border-orange-200 dark:hover:border-orange-800'
        };
      case 'red':
        return {
          iconBox: 'bg-red-100 dark:bg-red-900/20 text-red-600 dark:text-red-400',
          hover: 'hover:bg-red-50 dark:hover:bg-red-900/10',
          border: 'hover:border-red-200 dark:hover:border-red-800'
        };
    }
  };

  return (
    <div className="px-4">
      <div className="mb-8">
        <h1 className="text-2xl sm:text-3xl font-bold text-gray-900 dark:text-gray-100">Admin</h1>
        <p className="mt-1 text-sm text-gray-600 dark:text-gray-300">
          Quick links to administrative tools
        </p>
      </div>

      <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3">
        {adminCards.map((card) => {
          const colors = getColorClasses(card.colorScheme);
          return (
            <Link
              key={card.id}
              to={card.to}
              className={`
                group bg-white dark:bg-gray-800 shadow rounded-lg p-6 
                border border-gray-200 dark:border-gray-700 
                transition-all duration-200 
                ${colors.hover} ${colors.border}
                hover:shadow-md dark:hover:shadow-lg
              `}
            >
              <div className="flex items-start justify-between">
                <div className="flex-1">
                  <div className={`
                    inline-flex p-3 rounded-lg ${colors.iconBox}
                    group-hover:scale-105 transition-transform duration-200
                  `}>
                    {card.icon}
                  </div>
                  
                  <div className="mt-4">
                    <h3 className="text-lg font-medium text-gray-900 dark:text-gray-100 group-hover:text-gray-700 dark:group-hover:text-gray-200 flex items-center">
                      {card.title}
                      <svg 
                        className="ml-2 h-5 w-5 text-gray-400 group-hover:text-gray-600 dark:group-hover:text-gray-300 transition-colors" 
                        fill="none" 
                        stroke="currentColor" 
                        viewBox="0 0 24 24"
                      >
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M17 8l4 4m0 0l-4 4m4-4H3" />
                      </svg>
                    </h3>
                    <p className="mt-2 text-sm text-gray-500 dark:text-gray-400 group-hover:text-gray-600 dark:group-hover:text-gray-300">
                      {card.description}
                    </p>
                  </div>
                </div>
              </div>
            </Link>
          );
        })}
      </div>
    </div>
  );
}